import { createRootRoute, Outlet, useParams, useRouterState } from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import { AppSidebar } from '../components/AppSidebar'
import { AppBreadcrumb } from '../components/Breadcrumb'
import { SidebarProvider, SidebarInset } from '../components/ui/sidebar'
import { SidebarTriggerWithAttention } from '../components/NavAttention'
import { AuthProvider, useAuth } from '../contexts/AuthContext'
import { BreadcrumbProvider } from '../contexts/BreadcrumbContext'
import { LocaleProvider } from '../contexts/LocaleContext'
import { ThemeProvider } from '../contexts/ThemeContext'
import { LiveEventsGate } from '../components/LiveEventsGate'
import { PermissionsProvider, useMemberKind } from '../contexts/PermissionsContext'
import { AuthErrorBanner } from '../components/AuthErrorBanner'
import { ImpersonationBanner } from '../components/impersonation/ImpersonationBanner'
import { AppNotifications } from '../components/AppNotifications'
import { CommandPalette } from '../components/CommandPalette'
import { shellForKind } from '../lib/sidebar-menus'
import { ThemeToggle } from '../components/ThemeToggle'
import { LanguageToggle } from '../components/LanguageToggle'
import { LiveStatusIndicator } from '../components/LiveStatusIndicator'
import { AssistantGate } from '../components/assistant/AssistantGate'
import { ConversationNotificationsWatcher } from '../components/conversations/ConversationNotificationsWatcher'
import { FeedbackButton } from '../components/feedback/FeedbackButton'
import { FeedbackProvider, useFeedbackContext } from '../components/feedback/FeedbackContext'
import { mdiMagnify } from '@mdi/js'
import { AppIcon } from '../components/ui/icon'
import { PageErrorBoundary } from '../components/PageErrorBoundary'
import * as React from 'react'

export const Route = createRootRoute({
  component: RootComponent,
})

// isExternalCounterpartyRoute reports whether the current URL is a page served
// to someone OUTSIDE the tenant — the provider work-order portal
// (spec 2026-08-01-01), the public QR label scan page (spec 2026-08-17-03),
// the resident's own conversation page (spec 2026-09-02-05), the public page
// of a printed intake address (spec 2026-09-11-11), the
// invitation acceptance page (spec 2026-09-08-04), and the legal section at /about
// (spec 2026-09-09-02). Those pages must not get
// the application chrome: a contractor holding a capability link, a resident
// who just photographed a sticker in a hall, or someone accepting an
// invitation who has no account yet, has no organization and nowhere to
// navigate, so a sidebar toggle, a command palette, a live-updates indicator
// and an internal "Report an issue" button are at best confusing and at
// worst a leak of how the product is built.
//
// Read from window.location rather than router params because the root route
// renders above the matched route and has no params of its own.
function isExternalCounterpartyRoute(path: string): boolean {
  return (
    path.startsWith('/wo/') ||
    path.startsWith('/q/') ||
    path.startsWith('/c/') ||
    path.startsWith('/r/') ||
    // The signer of a signature request (spec 2026-10-05-05).
    path.startsWith('/s/') ||
    // The remote ballot of a co-owner (spec 2026-10-05-08): no account, no chrome.
    path.startsWith('/v/') ||
    // The daily digest's unsubscribe page (spec 2026-10-27-23).
    path.startsWith('/u/') ||
    path.startsWith('/invitations/') ||
    // Crash-confinement e2e fixture (spec 2026-10-06-09): a bare page that throws.
    path.startsWith('/test/bare/') ||
    path === '/about' ||
    path.startsWith('/about/')
  )
}

// isMfaRoute: the full-screen two-step verification pages (spec
// 2026-10-01-03). Their session reaches nothing but these pages' endpoints,
// so the app chrome (sidebar, live events, notifications) would only collect
// 403s. Reached by client-side navigation, hence the router state below
// rather than a one-off read of window.location.
function isMfaRoute(path: string): boolean {
  return path === '/mfa-setup' || path === '/mfa-verify'
}

// isImpersonationRoute: the page redeeming an impersonation link (spec
// 2026-10-04-03), opened in a browser that may hold no session at all.
function isImpersonationRoute(path: string): boolean {
  return path.startsWith('/impersonate/')
}

function RootComponent() {
  const path = useRouterState({ select: (s) => s.location.pathname })
  return (
    <ThemeProvider>
      <AuthProvider>
        {isExternalCounterpartyRoute(path) || isMfaRoute(path) || isImpersonationRoute(path) ? <BareShell /> : <AppShell />}
      </AuthProvider>
    </ThemeProvider>
  )
}

// BareShell is the whole layout for an external counterparty: locale handling
// (the page is FR/EN) and the routed content. Nothing else.
function BareShell() {
  const path = useRouterState({ select: (s) => s.location.pathname })
  return (
    <LocaleProvider>
      <PageErrorBoundary resetKey={path}>
        <Outlet />
      </PageErrorBoundary>
    </LocaleProvider>
  )
}

// PermissionsGate mounts PermissionsProvider for the same reason, and with the
// same loose org read, as LiveEventsGate above: AppSidebar hides entries the
// caller may not reach, and it renders BESIDE the routed content rather than
// inside it, so a provider scoped to routes/$org.tsx could never reach it.
//
// Outside an org (/organizations, /no-organization, /login) there is no
// membership and nothing org-scoped to gate, so children render with the
// context's empty default — consumers there check for the org themselves.
function PermissionsGate({ children }: { children: React.ReactNode }) {
  const { org } = useParams({ strict: false })
  if (!org) return <>{children}</>
  return <PermissionsProvider org={org}>{children}</PermissionsProvider>
}

function BrowserNotificationsGate() {
  const { org } = useParams({ strict: false })
  if (!org) return null
  return <ConversationNotificationsWatcher org={org} />
}

// FeedbackProvider owns the single useFeedback() session and renders the one
// <FeedbackDialog>, so both the desktop header trigger and the mobile
// sidebar-footer trigger (AppSidebar) drive the same state. It sits inside
// LocaleProvider because the dialog is translated.
function AppShell() {
  return (
    <LocaleProvider>
      <FeedbackProvider>
        <AppShellContent />
      </FeedbackProvider>
    </LocaleProvider>
  )
}

function AppShellContent() {
  const path = useRouterState({ select: (s) => s.location.pathname })
  const showDevTools = new URLSearchParams(window.location.search).get('dev') === 'true'
  const [commandPaletteOpen, setCommandPaletteOpen] = React.useState(false)
  const { isCapturing, isRecording, open: openFeedback } = useFeedbackContext()

  // CMD+K / Ctrl+K opens the command palette;
  // CMD+Shift+B / Ctrl+Shift+B opens the feedback dialog ("B" for Bug).
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault()
        setCommandPaletteOpen(true)
        return
      }
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === 'b' || e.key === 'B')) {
        e.preventDefault()
        openFeedback()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [openFeedback])

  return (
    <>
      <LiveEventsGate>
       <PermissionsGate>
        <BrowserNotificationsGate />
        <BreadcrumbProvider>
          <SidebarProvider>
            <AppSidebar />
            <SidebarInset className="overflow-x-hidden">
              <ImpersonationBanner />
              <AuthErrorBannerContainer />
              <AppNotifications />
              <header className="flex h-12 shrink-0 items-center gap-2 border-b px-4">
                <SidebarTriggerWithAttention className="-ml-1" />
                <AppBreadcrumb />
                <div className="ml-auto flex items-center gap-1" data-testid="header-actions">
                  <ShellSearchOnly>
                    <button
                      onClick={() => {
                        setCommandPaletteOpen(true)
                      }}
                      className="p-2 hover:bg-accent rounded-md transition-colors"
                      data-testid="search-button"
                    >
                      <AppIcon path={mdiMagnify} size="sm" />
                    </button>
                  </ShellSearchOnly>
                  {/* LiveStatusIndicator sits here, OUTSIDE the `hidden
                      md:flex` group below, on purpose (spec 2026-09-12-03):
                      it is the only way a phone user — who never opens the
                      sidebar Sheet — learns the live socket dropped. Do not
                      fold it into that group or move it into the sidebar to
                      "match" solidping; both would hide it below 768px. */}
                  <LiveStatusIndicator />
                  {/* The assistant (spec 2026-10-21-24), outside the desktop-only
                      group too, so phones get it. */}
                  <AssistantGate />
                  {/* Feedback, language, and theme toggles are desktop-only.
                      Mobile users get the same three controls in the sidebar
                      footer's `sidebar-mobile-toggles` row (AppSidebar). */}
                  <div className="hidden md:flex items-center gap-1" data-testid="header-desktop-actions">
                    <FeedbackButton onClick={openFeedback} isCapturing={isCapturing} isRecording={isRecording} />
                    <LanguageToggle />
                    <ThemeToggle />
                  </div>
                </div>
              </header>
              <div className="flex flex-1 flex-col gap-4 p-4">
                <PageErrorBoundary resetKey={path}>
                  <Outlet />
                </PageErrorBoundary>
              </div>
              {showDevTools && <TanStackRouterDevtools />}
            </SidebarInset>
            <ShellSearchOnly>
              <CommandPalette
                open={commandPaletteOpen}
                onOpenChange={setCommandPaletteOpen}
              />
            </ShellSearchOnly>
          </SidebarProvider>
        </BreadcrumbProvider>
       </PermissionsGate>
      </LiveEventsGate>
    </>
  )
}

// ShellSearchOnly renders the global search (header button and command
// palette) only for the member kinds whose shell has one: a resident's menu
// has no Search (lib/sidebar-menus.ts, spec 2026-10-07-03), and the palette
// would lead them to sections the route guard bounces.
function ShellSearchOnly({ children }: { children: React.ReactNode }) {
  const kind = useMemberKind()
  if (!shellForKind(kind).search) return null

  return <>{children}</>
}

function AuthErrorBannerContainer() {
  const { showAuthErrorBanner, dismissAuthErrorBanner, getLoginUrl, sessionEndedReason } = useAuth()

  if (!showAuthErrorBanner) {
    return null
  }

  return (
    <AuthErrorBanner
      onDismiss={dismissAuthErrorBanner}
      loginUrl={getLoginUrl()}
      reason={sessionEndedReason}
    />
  )
}